import { isLayerIdAllowingMultipleSelections, pickInfoPriority, ZOOM_THRESHOLDS } from '../consts';
import {
    PickInfoToPrioritize,
    isAreaEmissionInfo,
    isPlumeEmissionInfo,
    isTargetInfo,
    STACCollection,
    ActiveCapture,
    TotalOnlyInfo,
    isTotalOnlyInfo
} from '../types';
import { stacAPISlice } from '../reducers';
import { STACTileLayer } from '@methanesat/maps';
import type { AppDispatch } from '../store';
import { centroid, polygon } from '@turf/turf';
import { getStacPlumeTimes } from './stac';
import { getRootId } from './featureHelpers';

export function isNoData<Info extends PickInfoToPrioritize>(pickInfo: Info) {
    const feature = pickInfo?.object;
    if (feature) {
        return feature.properties?.methane === null;
    }
    return false;
}

/**
 * From an array of pickInfos, return an array of pickInfos that match the given layerId.
 *
 * For any layer to allow multiple features to be selected at a time, the layer id must be included
 * in `LAYERS_ALLOWING_MULTIPLE_FEATURE_SELECTION`.
 *
 * Each deck.gl must also be set up specifically to handle highlighting multiple features at once
 * (see `selectInfrastructureTileLayer` in file `app/src/reducers/pages/emissions-map.ts`)
 */
export const pickMultiplePrioritizedFilteredLayers = <Info extends PickInfoToPrioritize>(
    pickInfos: Info[],
    layerId?: string
) => {
    if (!layerId || !isLayerIdAllowingMultipleSelections(layerId)) return null;
    return (
        pickInfos.filter((pickInfo) => {
            return pickInfo.layer.id === layerId && typeof pickInfo.object === 'object' && pickInfo.object;
        }) || null
    );
};

/**
 * From an array of pick infos, returns an array of valid infos sorted by priority.
 * An info is invalid if it has an invalid info.object, negative methane data, or bad/missing raster data.
 */
export const getValidSortedPickInfos = async (
    infoList: PickInfoToPrioritize[],
    zoom: number,
    dispatch: AppDispatch
): Promise<PickInfoToPrioritize[] | undefined> => {
    if (!infoList.length) return;

    const processedInfos = await Promise.all(
        infoList.map(async (info) => {
            // bitmap handling
            if (info.bitmap) {
                const coordinates = info.coordinate;
                const { itemId, collectionId } = info.layer as STACTileLayer;
                const promise = await dispatch(
                    stacAPISlice.endpoints.getRasterPointFeature.initiate({
                        coordinates,
                        itemId,
                        collectionId: collectionId as STACCollection
                    })
                );
                const { data: rasterData } = promise;
                const updatedInfo = {
                    ...info,
                    object: { ...rasterData, collection: collectionId }
                } as PickInfoToPrioritize;
                if (rasterData && !isNoData(updatedInfo)) {
                    return updatedInfo;
                }
            }
            // non-bitmap handling
            else if (typeof info.object === 'object' && info.object && !isNoData(info)) {
                return info;
            }
        })
    );

    // Filter out undefined values, sort, then dedupe
    const sorted = sortPickInfos(
        processedInfos.filter((i) => !!i),
        zoom
    );
    return dedupePickInfos(sorted);
};

/**
 * From an array of pick infos, returns the pick info based on the priority order.
 * @example
 * sortPickInfos([{layer: {id: 'unknown'}}, {layer: {id: OGILayerIds.pointInfrastructure}}])
 * // returns [{layer: {id: OGILayerIds.pointInfrastructure}}, {layer: {id: 'unknown'}}]
 */
export const sortPickInfos = <Info extends PickInfoToPrioritize>(pickInfos: Info[], zoom: number): Info[] => {
    let zoomPickInfoPriority = pickInfoPriority;
    // don't allow picking a target feature when rasters are visible.
    if (zoom >= ZOOM_THRESHOLDS.MINIMUM_ZOOM_LEVEL_AREA_FLUX_GRID) zoomPickInfoPriority = pickInfoPriority.slice(1);
    return (
        pickInfos
            // order the remaining infos by priority
            .sort(({ layer: { id: idA } }, { layer: { id: idB } }) => {
                const priorityA = zoomPickInfoPriority.indexOf(getRootId(idA));
                const priorityB = zoomPickInfoPriority.indexOf(getRootId(idB));
                // unknown (un-prioritized) layers should always be moved to the end
                if (priorityA === -1) return 1;
                if (priorityB === -1) return -1;
                return priorityA - priorityB;
            })
    );
};

/**
 * Removes duplicate pick infos from an array of infos ( deck.gl sometimes sends duplicates on map click)
 */
export const dedupePickInfos = (pickInfos: PickInfoToPrioritize[]) => {
    const seen = new Set();
    return pickInfos.filter((info) => {
        if (info.object.geometry.type === 'GeometryCollection' || !info.object.geometry) return false;

        const coords = info.object.geometry.coordinates;
        const key = `${info.layer.id}-${coords.join(',')}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

/**
 * Gets ActiveCapture props from a plume, dispersed or target PickInfo
 */
export const getActiveCaptureParams = (info: PickInfoToPrioritize | TotalOnlyInfo): ActiveCapture => {
    if (isPlumeEmissionInfo(info) /*  && info.object.relatedRasterItemId */)
        return {
            // special handling for MAIR points that don't have related totals and
            // therefore don't have a relatedRasterItemId
            itemId: info.object.relatedRasterItemId || '',
            targetId: info.object.parentTargetId
        };
    if (isAreaEmissionInfo(info) || isTotalOnlyInfo(info))
        return {
            itemId: info.object.id,
            targetId: info.object.properties.target_id
        };
    if (isTargetInfo(info))
        return {
            itemId: info.object.id,
            targetId: info.object.properties.target_id
        };
    throw new Error(`Unexpected map info ${JSON.stringify({ object: info.object }, null, 2)}`);
};

/**
 * Used to structure data for selected emissions params,
 * used in to `setSelectedFeatureParams`
 */
export const getEmissionSelectionParams = (prioritizedInfo: PickInfoToPrioritize) => {
    if (isAreaEmissionInfo(prioritizedInfo)) {
        return {
            featureCoordinates: prioritizedInfo.coordinate,
            featureDate: prioritizedInfo.object.properties?.collectionStartTime,
            featureItemId: prioritizedInfo.layer.itemId,
            featureTargetId: prioritizedInfo.layer.parentTargetId,
            collectionId: prioritizedInfo.object.properties.collectionId
        };
    }
    if (isTotalOnlyInfo(prioritizedInfo)) {
        return {
            featureCoordinates: prioritizedInfo.coordinate,
            featureDate: prioritizedInfo.object.properties.start_datetime,
            featureItemId: prioritizedInfo.object.id,
            featureTargetId: prioritizedInfo.object.properties.target_id,
            collectionId: prioritizedInfo.object.collection
        };
    }
    if (isPlumeEmissionInfo(prioritizedInfo)) {
        return {
            featureCoordinates: prioritizedInfo.object.geometry.coordinates,
            featureDate: getStacPlumeTimes(prioritizedInfo.object.properties).startTime,
            featureItemId: prioritizedInfo.object.itemId,
            featureTargetId: prioritizedInfo.object.parentTargetId,
            relatedRasterItemId: prioritizedInfo.object.relatedRasterItemId,
            collectionId: prioritizedInfo.object.collection
        };
    }
    if (isTargetInfo(prioritizedInfo)) {
        return {
            featureCoordinates: centroid(polygon(prioritizedInfo.object.geometry.coordinates)).geometry.coordinates,
            featureDate: prioritizedInfo.object.properties?.start_datetime,
            featureItemId: prioritizedInfo.object.id,
            featureTargetId: prioritizedInfo.object.properties.target_id
        };
    }
    // should never reach this, but this will handle a default/fallback case
    throw new Error(`Unsupported map info`);
};
